/**
 * Server functions for the admin coach directory at `/admin/coaches`. Each one
 * is admin gated and delegates to `~/lib/server/admin-coach-directory`.
 *
 * Query keys live under `["admin", "coaches"]`, so anything that changes a
 * coach (approvals, the URL dialog) refreshes the directory by invalidating
 * that prefix.
 *
 * Aliases: admin coach directory server functions, coach list export, coach
 * directory summary
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  coachDirectoryExportInputSchema,
  coachDirectoryListInputSchema,
  coachDirectorySummaryInputSchema,
  type CoachDirectoryExport,
  type CoachDirectoryListInput,
  type CoachDirectoryPage,
  type CoachDirectorySummary,
} from "~/lib/shared/admin-coach-directory";
import {
  coachReadinessInputSchema,
  type CoachReadinessByCoach,
} from "~/lib/shared/admin-coach-readiness";

export const adminCoachDirectoryQueryKeys = {
  all: ["admin", "coaches"] as const,
  readiness: (coachIds: readonly string[]) =>
    ["admin", "coaches", "readiness", coachIds] as const,
  list: (input: CoachDirectoryListInput) =>
    ["admin", "coaches", "list", input] as const,
  summary: (search: string) => ["admin", "coaches", "summary", search] as const,
};

export const listAdminCoachDirectoryFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:read")])
  .validator(coachDirectoryListInputSchema)
  .handler(async ({ data, context }): Promise<CoachDirectoryPage> => {
    if (data.sort === "readiness") {
      const { requirePermissionForClerkUser } =
        await import("~/lib/server/permissions");
      await requirePermissionForClerkUser(
        context.user.clerkUserId,
        "coach-assessments:read",
      );
    }
    const { listCoachDirectory } =
      await import("~/lib/server/admin-coach-directory");
    return listCoachDirectory(data);
  });

export const getAdminCoachDirectorySummaryFn = createServerFn({
  method: "POST",
})
  .middleware([permissionMiddleware("coaches:read")])
  .validator(coachDirectorySummaryInputSchema)
  .handler(async ({ data }): Promise<CoachDirectorySummary> => {
    const { summarizeCoachDirectory } =
      await import("~/lib/server/admin-coach-directory");
    return summarizeCoachDirectory(data.search);
  });

export const exportAdminCoachDirectoryFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("coaches:read")])
  .validator(coachDirectoryExportInputSchema)
  .handler(async ({ data, context }): Promise<CoachDirectoryExport> => {
    if (data.sort === "readiness") {
      const { requirePermissionForClerkUser } =
        await import("~/lib/server/permissions");
      await requirePermissionForClerkUser(
        context.user.clerkUserId,
        "coach-assessments:read",
      );
    }
    const { exportCoachDirectory } =
      await import("~/lib/server/admin-coach-directory");
    return exportCoachDirectory(data);
  });

/**
 * Readiness results need `coach-assessments:read` beside `coaches:read`; the
 * directory's own calls carry none of it.
 */
export const getAdminCoachReadinessFn = createServerFn({ method: "POST" })
  .middleware([
    permissionMiddleware("coaches:read"),
    permissionMiddleware("coach-assessments:read"),
  ])
  .validator(coachReadinessInputSchema)
  .handler(async ({ data }): Promise<CoachReadinessByCoach> => {
    const { readCoachReadiness } =
      await import("~/lib/server/admin-coach-readiness");
    return readCoachReadiness(data.coachIds);
  });

export function adminCoachReadinessQueryOptions(coachIds: readonly string[]) {
  return queryOptions({
    queryKey: adminCoachDirectoryQueryKeys.readiness(coachIds),
    queryFn: () =>
      getAdminCoachReadinessFn({ data: { coachIds: [...coachIds] } }),
  });
}

export function adminCoachDirectoryListQueryOptions(
  input: CoachDirectoryListInput,
) {
  return queryOptions({
    queryKey: adminCoachDirectoryQueryKeys.list(input),
    queryFn: () => listAdminCoachDirectoryFn({ data: input }),
  });
}

export function adminCoachDirectorySummaryQueryOptions(search: string) {
  return queryOptions({
    queryKey: adminCoachDirectoryQueryKeys.summary(search),
    queryFn: () => getAdminCoachDirectorySummaryFn({ data: { search } }),
  });
}
